
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Search,
  Filter,
  ArrowUpDown,
  MoreVertical,
  X,
  CheckCircle2,
  Clock,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  FileUp,
  Layers,
  Target,
  ShieldCheck,
  Zap,
  Banknote,
  Edit3,
  Trash2,
  ChevronRight,
  ChevronDown,
  History,
  Cpu,
  Info,
  BarChart3,
  Loader2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { cn, logInteraction } from '../lib/utils';
import { Order, OrderStatus, OrderSide, OrderType, TimeInForce, RoutingPreference } from '../types/oms';
import { omsService } from '../services/omsService';
import { useExecutionStore } from '../hooks/useExecutionStore';
import { useSystemStatus } from '../hooks/useSystemStatus';
import type { TradeSide, TradeOrderType } from '../types/execution';
import { safeFormatTimestamp } from '../lib/accountFormatters';

export default function OMSModule() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isTicketOpen, setIsTicketOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'active' | 'history' | 'bulk' | 'execution'>('active');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isModifying, setIsModifying] = useState(false);
  const [modQuantity, setModQuantity] = useState('');
  const [modPrice, setModPrice] = useState('');
  const [modError, setModError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedOrder) {
      setModQuantity(selectedOrder.quantity.toString());
      setModPrice(selectedOrder.price?.toString() || '');
      setModError(null);
    }
  }, [selectedOrder]);

  const handleModifyOrder = () => {
    if (!selectedOrder) return;
    try {
      setModError(null);
      omsService.modifyOrder(selectedOrder.id, { 
        quantity: parseInt(modQuantity), 
        price: modPrice ? parseFloat(modPrice) : undefined 
      });
      setIsModifying(false);
      refreshOrders();
    } catch (err) {
      setModError(err instanceof Error ? err.message : 'Modification failed');
    }
  };

  const handleReplaceOrder = () => {
    if (!selectedOrder) return;
    try {
      setModError(null);
      // Replace = Cancel + New
      omsService.cancelOrder(selectedOrder.id);
      omsService.submitOrder({
        symbol: selectedOrder.symbol,
        side: selectedOrder.side,
        type: selectedOrder.type,
        quantity: parseInt(modQuantity),
        price: modPrice ? parseFloat(modPrice) : undefined,
        stopPrice: selectedOrder.stopPrice,
        timeInForce: selectedOrder.timeInForce,
        routing: selectedOrder.routing
      });
      setIsModifying(false);
      refreshOrders();
    } catch (err) {
      setModError(err instanceof Error ? err.message : 'Replacement failed');
    }
  };

  useEffect(() => {
    setOrders(omsService.getOrders());
  }, []);

  const refreshOrders = () => {
    setOrders(omsService.getOrders());
  };

  const handleCancelOrder = (id: string) => {
    try {
      omsService.cancelOrder(id);
      refreshOrders();
    } catch (err) {
      void 0 /* alert removed */
    }
  };

  const StatusBadge = ({ status }: { status: OrderStatus }) => {
    const styles = {
      OPEN: "bg-[var(--input-bg)] text-[var(--muted-text)] border-[var(--input-border)]",
      PARTIALLY_FILLED: "bg-amber-500/10 text-amber-600 border-amber-500/20",
      FILLED: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
      CANCELLED: "bg-rose-500/10 text-rose-600 border-rose-500/20",
      REJECTED: "bg-rose-500/10 text-rose-600 border-rose-500/20",
      PENDING: "bg-[var(--input-bg)] text-[var(--muted-text)] opacity-50 border-[var(--input-border)]",
      EXPIRED: "bg-[var(--input-bg)] text-[var(--muted-text)] border-[var(--input-border)]",
    };

    return (
      <span className={cn("px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest border", styles[status])}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  return (
    <div className="app-container space-y-6 pb-12">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-[var(--app-text)] tracking-tight uppercase italic font-display">Order Management System</h1>
          <p className="text-[var(--muted-text)] mt-2 max-w-2xl font-medium">
            Institutional-grade OMS with smart routing, pre-trade risk validation, and multi-venue execution tracking.
          </p>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Blotter Section */}
        <div className="lg:col-span-8 space-y-6">
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-[var(--card-header-border)] flex items-center justify-between bg-[var(--card-header-bg)]">
              <div className="flex items-center gap-4">
                <div className="flex bg-[var(--input-bg)] p-1 rounded-lg">
                  <button 
                    onClick={() => {
                      if (activeTab === 'active') return;
                      setActiveTab('active');
                    }}
                    className={cn(
                      "px-4 py-1.5 rounded-md text-[10px] font-black uppercase tracking-widest transition-all border",
                      activeTab === 'active' 
                        ? "bg-[var(--card-bg)] text-[var(--app-text)] shadow-[0_0_10px_rgba(var(--app-text-rgb),0.1)] border-[var(--card-border)]" 
                        : "text-[var(--muted-text)] hover:text-[var(--app-text)] border-transparent"
                    )}
                  >
                    Active Blotter
                  </button>
                  <button 
                    onClick={() => {
                      if (activeTab === 'history') return;
                      setActiveTab('history');
                    }}
                    className={cn(
                      "px-4 py-1.5 rounded-md text-[10px] font-black uppercase tracking-widest transition-all border",
                      activeTab === 'history' 
                        ? "bg-[var(--card-bg)] text-[var(--app-text)] shadow-[0_0_10px_rgba(var(--app-text-rgb),0.1)] border-[var(--card-border)]" 
                        : "text-[var(--muted-text)] hover:text-[var(--app-text)] border-transparent"
                    )}
                  >
                    Order History
                  </button>
                  <button 
                    onClick={() => {
                      if (activeTab === 'execution') return;
                      setActiveTab('execution');
                    }}
                    className={cn(
                      "px-4 py-1.5 rounded-md text-[10px] font-black uppercase italic transition-all border",
                      activeTab === 'execution' 
                        ? "bg-[var(--card-bg)] text-emerald-600 shadow-[0_0_10px_rgba(16,185,129,0.1)] border-emerald-500/30" 
                        : "text-[var(--muted-text)] hover:text-[var(--app-text)] border-transparent"
                    )}
                  >
                    Execution Analytics
                  </button>
                  <button 
                    onClick={() => {
                      if (activeTab === 'bulk') return;
                      setActiveTab('bulk');
                    }}
                    className={cn(
                      "px-4 py-1.5 rounded-md text-[10px] font-black uppercase tracking-widest transition-all border",
                      activeTab === 'bulk' 
                        ? "bg-[var(--card-bg)] text-[var(--app-text)] shadow-[0_0_10px_rgba(var(--app-text-rgb),0.1)] border-[var(--card-border)]" 
                        : "text-[var(--muted-text)] hover:text-[var(--app-text)] border-transparent"
                    )}
                  >
                    Bulk Entry
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {activeTab === 'bulk' && (
                  <button 
                    onClick={() => void 0 /* alert removed */}
                    className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-emerald-700 transition-all"
                  >
                    <FileUp size={14} />
                    Upload CSV
                  </button>
                )}
                <div className="relative group">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-text)]" size={14} />
                  <input 
                    type="text" 
                    placeholder="Filter by symbol, ID..." 
                    className="bg-[var(--input-bg)] border border-[var(--input-border)] rounded-lg pl-9 pr-4 py-1.5 text-xs font-medium text-[var(--app-text)] outline-none focus:border-emerald-600 transition-all w-48"
                  />
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              {activeTab === 'bulk' ? (
                <div className="p-12 flex flex-col items-center justify-center text-center space-y-6">
                  <div className="w-16 h-16 bg-[var(--input-bg)] rounded-2xl flex items-center justify-center border border-[var(--input-border)]">
                    <Layers className="text-[var(--muted-text)] opacity-30" size={32} />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-[var(--app-text)] uppercase italic">Institutional Basket Trading</h3>
                    <p className="text-[var(--muted-text)] text-sm mt-2 max-w-md font-medium">
                      Upload a CSV or paste a list of orders to execute bulk batch trades across multiple venues.
                    </p>
                  </div>
                  <div className="flex gap-4">
                    <button 
                      onClick={() => void 0 /* alert removed */}
                      className="px-6 py-3 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl text-xs font-black uppercase italic hover:bg-[var(--input-bg)] transition-all shadow-sm text-[var(--app-text)]"
                    >
                      Download Template
                    </button>
                    <button 
                      onClick={() => void 0 /* alert removed */}
                      className="px-6 py-3 bg-emerald-600 text-white rounded-xl text-xs font-black uppercase italic hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-600/20"
                    >
                      Select File
                    </button>
                  </div>
                </div>
              ) : activeTab === 'execution' ? (
                <ExecutionManagementSystem />
              ) : activeTab === 'history' && orders.filter(o => ['FILLED', 'CANCELLED', 'REJECTED'].includes(o.status)).length === 0 ? (
                <div className="p-12 flex flex-col items-center justify-center text-center space-y-4">
                  <div className="w-12 h-12 bg-[var(--input-bg)] rounded-xl flex items-center justify-center text-[var(--muted-text)] opacity-30">
                    <History size={24} />
                  </div>
                  <p className="text-[var(--muted-text)] text-sm font-medium uppercase tracking-widest">No historical orders found</p>
                </div>
              ) : (
                <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-[var(--card-header-bg)] border-b border-[var(--card-header-border)]">
                    <th className="px-6 py-3 text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Symbol</th>
                    <th className="px-6 py-3 text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Type</th>
                    <th className="px-6 py-3 text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Quantity</th>
                    <th className="px-6 py-3 text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Price</th>
                    <th className="px-6 py-3 text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Route</th>
                    <th className="px-6 py-3 text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Status</th>
                    <th className="px-6 py-3 text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Submitted</th>
                    <th className="px-6 py-3 text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Filled</th>
                    <th className="px-6 py-3 text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders
                    .filter(o => activeTab === 'active' ? !['FILLED', 'CANCELLED', 'REJECTED'].includes(o.status) : ['FILLED', 'CANCELLED', 'REJECTED'].includes(o.status))
                    .map((order) => (
                    <tr key={order.id} className="border-b border-[var(--card-border)] hover:bg-[var(--input-bg)] transition-colors group">
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-black text-[var(--app-text)] italic">{order.symbol}</span>
                          <span className={cn(
                            "text-[10px] font-black uppercase italic",
                            order.side === 'BUY' ? "text-emerald-600" : "text-rose-600"
                          )}>
                            {order.side}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs font-bold text-[var(--muted-text)] uppercase tracking-widest">{order.type}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-xs font-black text-[var(--app-text)] italic">{order.quantity}</span>
                          <div className="w-16 h-1 bg-[var(--input-bg)] rounded-full mt-1 overflow-hidden">
                            <div 
                              className="h-full bg-emerald-500" 
                              style={{ width: `${(order.filledQuantity / order.quantity) * 100}%` }}
                            />
                          </div>
                          <span className="text-[10px] text-[var(--muted-text)] font-bold mt-0.5">{order.filledQuantity} filled</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs font-black text-[var(--app-text)] italic">
                          {order.type === 'MARKET' ? 'MKT' : `$${order.price?.toFixed(2)}`}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest bg-[var(--input-bg)] px-1.5 py-0.5 rounded">
                          {order.routing}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={order.status} />
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-[10px] font-mono font-bold text-[var(--muted-text)] uppercase">
                          {safeFormatTimestamp(order.timestamp, { mode: 'time', fallback: '—', invalidFallback: '—' })}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-[10px] font-mono font-bold text-[var(--muted-text)] uppercase">
                          {order.status === 'FILLED' ? safeFormatTimestamp(order.timestamp, { mode: 'time', fallback: '--', invalidFallback: '--' }) : '--'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {!['FILLED', 'CANCELLED', 'REJECTED'].includes(order.status) && (
                            <>
                              <button 
                                onClick={() => { setSelectedOrder(order); setIsModifying(true); }}
                                className="p-1.5 text-[var(--muted-text)] hover:text-emerald-600 hover:bg-emerald-500/10 rounded-lg transition-all"
                                title="Modify / Replace"
                              >
                                <Edit3 size={14} />
                              </button>
                              <button 
                                onClick={() => handleCancelOrder(order.id)}
                                className="p-1.5 text-[var(--muted-text)] hover:text-rose-600 hover:bg-rose-500/10 rounded-lg transition-all"
                                title="Cancel Order"
                              >
                                <Trash2 size={14} />
                              </button>
                            </>
                          )}
                          <button 
                            onClick={() => void 0 /* alert removed */}
                            className="p-1.5 text-[var(--muted-text)] hover:text-[var(--app-text)] rounded-lg transition-all"
                          >
                            <MoreVertical size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              )}
            </div>
          </div>
        </div>

        {/* Order Entry Panel (Right Side) */}
        <div className="lg:col-span-4 sticky top-24">
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl shadow-sm overflow-hidden">
            <OrderTicket refreshOrders={refreshOrders} />
          </div>
        </div>
      </div>

      {/* Order Ticket Modal */}
      <AnimatePresence>
        {isTicketOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsTicketOpen(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-lg bg-[var(--card-bg)] rounded-2xl shadow-2xl overflow-hidden"
            >
              <OrderTicket refreshOrders={refreshOrders} onClose={() => setIsTicketOpen(false)} />
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modification Modal */}
      <AnimatePresence>
        {isModifying && selectedOrder && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModifying(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-lg bg-[var(--card-bg)] rounded-2xl shadow-2xl p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-black text-[var(--app-text)] uppercase italic">Modify Order {selectedOrder.id}</h3>
                <button onClick={() => setIsModifying(false)} className="text-[var(--muted-text)] hover:text-[var(--app-text)]">
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                {modError && (
                  <div className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl flex gap-2 items-center text-rose-600">
                    <AlertCircle size={16} />
                    <span className="text-xs font-bold uppercase tracking-tight">{modError}</span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Quantity</label>
                    <input 
                      type="number" 
                      value={modQuantity}
                      onChange={(e) => setModQuantity(e.target.value)}
                      className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2.5 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Price</label>
                    <input 
                      type="number" 
                      value={modPrice}
                      onChange={(e) => setModPrice(e.target.value)}
                      className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2.5 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600"
                    />
                  </div>
                </div>
                
                <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl flex gap-3">
                  <AlertCircle className="text-amber-600 shrink-0" size={18} />
                  <p className="text-xs text-amber-600 font-medium leading-relaxed">
                    Modifying an order will cancel the existing order and replace it with a new one. This may affect your priority in the order book.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-4">
                  <button 
                    onClick={handleModifyOrder}
                    className="py-3 border border-[var(--card-border)] text-[var(--muted-text)] rounded-xl text-xs font-black uppercase italic hover:bg-[var(--input-bg)] transition-all"
                  >
                    Modify Price/Qty
                  </button>
                  <button 
                    onClick={handleReplaceOrder}
                    className="py-3 bg-emerald-600 text-white rounded-xl text-xs font-black uppercase italic hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-600/20"
                  >
                    Cancel & Replace
                  </button>
                </div>
                <button 
                  onClick={() => {
                    handleCancelOrder(selectedOrder.id);
                    setIsModifying(false);
                  }}
                  className="w-full py-3 bg-rose-500/10 text-rose-600 rounded-xl text-xs font-black uppercase italic hover:bg-rose-500/20 transition-all"
                >
                  Cancel Order
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ExecutionManagementSystem() {
  const [routingMode, setRoutingMode] = useState('Smart Route (SOR)');
  const [algoMode, setAlgoMode] = useState('VWAP');
  const [analyticsTimeframe, setAnalyticsTimeframe] = useState('1D');

  return (
    <div className="p-6 space-y-8">
      {/* Execution Monitor */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Execution Venue', value: 'SMART (IEX/NSDQ)', icon: Target },
          { label: 'Avg Latency', value: '12.4ms', icon: Zap, color: 'text-emerald-600' },
          { label: 'Fill Quality', value: '98.2%', icon: CheckCircle2, color: 'text-emerald-600' },
          { label: 'Avg Slippage', value: '0.02 bps', icon: ArrowDownRight, color: 'text-emerald-600' },
        ].map((stat, i) => (
          <div key={i} className="bg-[var(--input-bg)] border border-[var(--input-border)] rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <stat.icon size={14} className="text-[var(--muted-text)]" />
              <span className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">{stat.label}</span>
            </div>
            <div className={cn("text-sm font-black italic", stat.color || "text-[var(--app-text)]")}>{stat.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Smart Routing Panel */}
        <div className="space-y-4">
          <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic flex items-center gap-2">
            <Zap size={14} className="text-emerald-600" /> Smart Routing Configuration
          </h4>
          <div className="space-y-3">
            {[
              { label: 'Smart Route (SOR)', desc: 'Dynamic routing across all lit and dark venues.', active: routingMode === 'Smart Route (SOR)' },
              { label: 'Direct Route', desc: 'Bypass SOR and route directly to a specific exchange.', active: routingMode === 'Direct Route' },
              { label: 'Dark Pool Preference', desc: 'Prioritize hidden liquidity venues for large blocks.', active: routingMode === 'Dark Pool Preference' },
            ].map(option => (
              <button 
                key={option.label} 
                onClick={() => setRoutingMode(option.label)}
                className={cn(
                  "w-full p-4 rounded-xl border transition-all text-left group",
                  option.active ? "border-emerald-600 bg-emerald-500/10 shadow-sm" : "border-[var(--input-border)] hover:border-emerald-600/20"
                )}
              >
                <div className={cn(
                  "text-sm font-black uppercase italic",
                  option.active ? "text-emerald-600" : "text-[var(--app-text)]"
                )}>{option.label}</div>
                <div className="text-[10px] text-[var(--muted-text)] font-medium mt-1">{option.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Algorithmic Order Panel */}
        <div className="space-y-4">
          <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic flex items-center gap-2">
            <Cpu size={14} className="text-emerald-600" /> Algorithmic Execution Engines
          </h4>
          <div className="grid grid-cols-2 gap-3">
            {['VWAP', 'TWAP', 'Iceberg', 'POV (Pct of Vol)'].map(algo => (
              <button 
                key={algo} 
                onClick={() => setAlgoMode(algo)}
                className={cn(
                  "p-4 border rounded-xl transition-all text-center group",
                  algoMode === algo ? "border-emerald-500 bg-emerald-500/10" : "border-[var(--input-border)] hover:border-emerald-600 hover:bg-emerald-500/10"
                )}
              >
                <div className={cn(
                  "text-xs font-black uppercase italic",
                  algoMode === algo ? "text-emerald-600" : "text-[var(--app-text)] group-hover:text-emerald-600"
                )}>{algo}</div>
                <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase mt-1">{algoMode === algo ? 'Configured' : 'Configure'}</div>
              </button>
            ))}
          </div>
          <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl flex gap-3">
            <Info className="text-amber-600 shrink-0" size={18} />
            <p className="text-[10px] text-amber-600 font-medium leading-relaxed uppercase tracking-tight">
              Algorithmic orders are executed via Adversyn's low-latency co-located servers for maximum precision.
            </p>
          </div>
        </div>
      </div>

      {/* Execution Analytics Charts Placeholder */}
      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic">Execution Quality Analytics</h4>
          <div className="flex gap-2">
            {['1D', '1W', '1M', '1Y'].map(t => (
              <button 
                key={t} 
                onClick={() => setAnalyticsTimeframe(t)}
                className={cn(
                  "px-3 py-1 text-[10px] font-black uppercase border rounded-lg transition-all",
                  analyticsTimeframe === t ? "bg-[var(--app-text)] text-[var(--card-bg)] border-[var(--app-text)]" : "border-[var(--input-border)] hover:bg-[var(--input-bg)] text-[var(--muted-text)] hover:text-[var(--app-text)]"
                )}
              >{t}</button>
            ))}
          </div>
        </div>
        <div className="min-h-[320px] h-[320px] w-full flex items-center justify-center bg-[var(--input-bg)] rounded-xl border border-dashed border-[var(--input-border)]">
          <div className="text-center">
            <BarChart3 className="text-[var(--muted-text)] opacity-30 mx-auto mb-2" size={32} />
            <p className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Execution Performance Visualization</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-6 mt-6">
          <div className="text-center">
            <div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Avg Fill Speed</div>
            <div className="text-lg font-black text-[var(--app-text)] italic">42ms</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Price Improvement</div>
            <div className="text-lg font-black text-emerald-600 italic">+$1,420.50</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Exchange Quality</div>
            <div className="text-lg font-black text-[var(--app-text)] italic">A+ (IEX)</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function OrderTicket({
  refreshOrders,
  onClose,
  initialSymbol = '',
  initialPrice = '',
  initialSide = 'BUY'
}: {
  refreshOrders: () => void,
  onClose?: () => void,
  initialSymbol?: string,
  initialPrice?: string,
  initialSide?: OrderSide
}) {
  const [side, setSide] = useState<OrderSide>(initialSide);
  const [type, setType] = useState<OrderType>('LIMIT');
  const [tif, setTif] = useState<TimeInForce>('DAY');
  const [routing, setRouting] = useState<RoutingPreference>('SMART');
  const [symbol, setSymbol] = useState(initialSymbol);
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState(initialPrice);
  const [stopPrice, setStopPrice] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill symbol from search-bar selections
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { value?: string } | undefined;
      if (detail?.value) setSymbol(detail.value);
    };
    window.addEventListener('adversyn:symbol-selected', handler);
    return () => window.removeEventListener('adversyn:symbol-selected', handler);
  }, []);

  // Gateway execution store
  const systemStatus = useSystemStatus();
  // Execution mode from gateway
  const exec = useExecutionStore();

  // Map UI enums to API enums (lowercase)
  const mapSide = (s: OrderSide): TradeSide => s.toLowerCase() as TradeSide;
  const mapType = (t: OrderType): TradeOrderType => t.toLowerCase() as TradeOrderType;

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Submit to local OMS
      omsService.submitOrder({
        symbol: symbol.toUpperCase(),
        side,
        type,
        quantity: parseInt(quantity),
        price: price ? parseFloat(price) : undefined,
        stopPrice: stopPrice ? parseFloat(stopPrice) : undefined,
        timeInForce: tif,
        routing
      });
      refreshOrders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed');
      setIsSubmitting(false);
      return;
    }

    // Also get gateway preview (SIMULATION ONLY)
    logInteraction('OMS', 'Gateway Preview', { symbol, side, type, quantity });
    await exec.submitPreview({
      symbol: symbol.toUpperCase(),
      side: mapSide(side),
      order_type: mapType(type),
      quantity: parseInt(quantity),
      price: price ? parseFloat(price) : undefined,
      stop_price: stopPrice ? parseFloat(stopPrice) : undefined,
    });

    setIsSubmitting(false);
  }, [symbol, side, type, quantity, price, stopPrice, tif, routing, exec, refreshOrders]);

  const handleExecuteSimulation = useCallback(async () => {
    logInteraction('OMS', 'Execute Simulation', { previewId: exec.preview?.previewId });
    await exec.confirmExecution();
    refreshOrders();
  }, [exec, refreshOrders]);

  const handleResetExecution = useCallback(() => {
    exec.reset();
    setSymbol('');
    setQuantity('');
    setPrice('');
    setStopPrice('');
  }, [exec]);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Banknote size={20} className="text-emerald-600" />
          <h3 className="text-lg font-black text-[var(--app-text)] uppercase italic">New Order Ticket</h3>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-[var(--muted-text)] hover:text-[var(--app-text)]">
            <X size={20} />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Side Toggle */}
        <div className="flex bg-[var(--input-bg)] p-1 rounded-xl">
          <button
            type="button"
            onClick={() => setSide('BUY')}
            className={cn(
              "flex-1 py-2.5 rounded-lg text-xs font-black uppercase italic transition-all",
              side === 'BUY' ? "bg-emerald-600 text-white shadow-lg shadow-emerald-600/20" : "text-[var(--muted-text)] hover:text-[var(--app-text)]"
            )}
          >
            Buy
          </button>
          <button
            type="button"
            onClick={() => setSide('SELL')}
            className={cn(
              "flex-1 py-2.5 rounded-lg text-xs font-black uppercase italic transition-all",
              side === 'SELL' ? "bg-rose-600 text-white shadow-lg shadow-rose-600/20" : "text-[var(--muted-text)] hover:text-[var(--app-text)]"
            )}
          >
            Sell
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Symbol</label>
            <input
              type="text"
              required
              data-testid="oms-input-symbol"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              placeholder="e.g. NVDA"
              className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2.5 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600 uppercase italic"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Quantity</label>
            <input
              type="number"
              required
              data-testid="oms-input-quantity"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="0"
              className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2.5 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Order Type</label>
            <select
              value={type}
              data-testid="oms-select-type"
              onChange={(e) => {
                logInteraction('OMS', 'Change Order Type', { type: e.target.value });
                setType(e.target.value as OrderType);
              }}
              className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2.5 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600 uppercase italic cursor-pointer"
            >
              <option value="MARKET">Market</option>
              <option value="LIMIT">Limit</option>
              <option value="STOP">Stop</option>
              <option value="STOP_LIMIT">Stop Limit</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Limit Price</label>
            <input
              type="number"
              disabled={type === 'MARKET' || type === 'STOP'}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder={type === 'MARKET' ? 'Market Price' : '0.00'}
              className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2.5 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600 disabled:opacity-50"
            />
          </div>
        </div>

        {(type === 'STOP' || type === 'STOP_LIMIT') && (
          <div className="space-y-1">
            <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Stop Price</label>
            <input
              type="number"
              value={stopPrice}
              onChange={(e) => setStopPrice(e.target.value)}
              placeholder="0.00"
              className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2.5 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600"
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Time In Force</label>
            <select
              value={tif}
              onChange={(e) => {
                logInteraction('OMS', 'Change TIF', { tif: e.target.value });
                setTif(e.target.value as TimeInForce);
              }}
              className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2.5 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600 uppercase italic cursor-pointer"
            >
              <option value="DAY">Day</option>
              <option value="GTC">GTC</option>
              <option value="IOC">IOC</option>
              <option value="FOK">FOK</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Routing</label>
            <select
              value={routing}
              onChange={(e) => {
                logInteraction('OMS', 'Change Routing', { routing: e.target.value });
                setRouting(e.target.value as RoutingPreference);
              }}
              className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2.5 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600 uppercase italic cursor-pointer"
            >
              <option value="SMART">Smart Route</option>
              <option value="IEX">IEX</option>
              <option value="NASDAQ">NASDAQ</option>
              <option value="DARK_POOL">Dark Pool</option>
            </select>
          </div>
        </div>

        {/* Risk Check Summary */}
        <div className="p-4 bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl space-y-3">
          <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest">
            <span className="text-[var(--muted-text)]">Estimated Value</span>
            <span className="text-[var(--app-text)] font-black italic">
              {quantity && price ? `$${(parseInt(quantity) * parseFloat(price)).toLocaleString()}` : '--'}
            </span>
          </div>
          <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest">
            <span className="text-[var(--muted-text)]">AI Risk Rating</span>
            <span className="text-emerald-600 font-black italic flex items-center gap-1">
              <ShieldCheck size={12} /> Low Risk
            </span>
          </div>
        </div>

        {/* Error state */}
        {(error || exec.isError) && (
          <div data-testid="exec-error-panel" className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl flex gap-2 items-center text-rose-600">
            <AlertCircle size={16} />
            <span className="text-xs font-bold uppercase tracking-tight">{error ?? exec.error ?? 'Execution failed'}</span>
          </div>
        )}

        {/* Gateway Preview Results */}
        {exec.hasPreview && !exec.isSuccess && (
          <div data-testid="exec-preview-panel" className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-xl space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck size={14} className="text-emerald-600" />
              <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Simulation Preview</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest">
                <span className="text-[var(--muted-text)]">Est. Price</span>
                <span className="text-[var(--app-text)] font-black italic">${(exec.preview?.estimatedPrice ?? 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest">
                <span className="text-[var(--muted-text)]">Quantity</span>
                <span className="text-[var(--app-text)] font-black italic">{exec.preview?.quantity ?? 0}</span>
              </div>
              <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest">
                <span className="text-[var(--muted-text)]">Est. Total</span>
                <span className="text-[var(--app-text)] font-black italic">${(exec.preview?.estimatedTotal ?? 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest">
                <span className="text-[var(--muted-text)]">Fees Est.</span>
                <span className="text-[var(--app-text)] font-black italic">${(exec.preview?.feesEstimate ?? 0).toLocaleString()}</span>
              </div>
            </div>

            <div data-testid="exec-mode-badge" className={"p-3 rounded-lg flex gap-2 items-start " + (systemStatus.isSimulation ? "bg-amber-500/10 border border-amber-500/20" : systemStatus.isPaper ? "bg-blue-500/10 border border-blue-500/20" : "bg-rose-500/10 border border-rose-500/20")}>
              <Info size={14} className={systemStatus.isSimulation ? "text-amber-600 shrink-0 mt-0.5" : systemStatus.isPaper ? "text-blue-600 shrink-0 mt-0.5" : "text-rose-600 shrink-0 mt-0.5"} />
              <p className={"text-[10px] font-medium uppercase tracking-tight leading-relaxed " + (systemStatus.isSimulation ? "text-amber-600" : systemStatus.isPaper ? "text-blue-600" : "text-rose-600")}>
                {systemStatus.isSimulation && "SIMULATION MODE — No real capital will be used. No broker connections."}
                {systemStatus.isPaper && "PAPER MODE — Routed through broker abstraction. No real capital."}
                {systemStatus.isLive && systemStatus.safetyLock && "LIVE MODE BLOCKED — Safety lock active. ENABLE_LIVE_TRADING required."}
                {systemStatus.isLive && !systemStatus.safetyLock && "LIVE MODE — Real broker connected. Real capital at risk."}
              </p>
            </div>

            <button
              type="button"
              data-testid="exec-btn-execute-simulation"
              onClick={handleExecuteSimulation}
              disabled={exec.isExecuting}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-600/50 text-white rounded-xl text-sm font-black uppercase italic transition-all shadow-lg shadow-emerald-600/20 flex items-center justify-center gap-2"
            >
              {exec.isExecuting ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
              {exec.isExecuting ? 'Simulating...' : 'Execute Simulation'}
            </button>
          </div>
        )}

        {/* Execution Success */}
        {exec.isSuccess && exec.execution && (
          <div data-testid="exec-success-panel" className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-emerald-600" />
              <span className="text-xs font-black text-emerald-600 uppercase italic">Simulation Complete</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest">
                <span className="text-[var(--muted-text)]">Status</span>
                <span className={cn(
                  "font-black italic",
                  exec.execution.status === 'filled' ? "text-emerald-600" : "text-rose-600"
                )}>
                  {exec.execution.status?.toUpperCase() ?? 'N/A'}
                </span>
              </div>
              <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest">
                <span className="text-[var(--muted-text)]">Fill Price</span>
                <span className="text-[var(--app-text)] font-black italic">${(exec.execution.price ?? 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest">
                <span className="text-[var(--muted-text)]">Quantity</span>
                <span className="text-[var(--app-text)] font-black italic">{exec.execution.quantity ?? 0}</span>
              </div>
              <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest">
                <span className="text-[var(--muted-text)]">Order ID</span>
                <span className="text-[var(--app-text)] font-black italic text-[9px]">{exec.execution.executionId ?? 'N/A'}</span>
              </div>
            </div>
            <p className="text-[10px] text-[var(--muted-text)] font-medium italic">{exec.execution.message ?? ''}</p>
            <button
              type="button"
              onClick={handleResetExecution}
              className="w-full py-2 bg-[var(--input-bg)] text-[var(--muted-text)] rounded-xl text-[10px] font-black uppercase italic hover:bg-[var(--card-border)] transition-all"
            >
              New Order
            </button>
          </div>
        )}

        {/* Previewing state */}
        {exec.isPreviewing && (
          <div data-testid="exec-previewing" className="flex items-center justify-center gap-2 py-3">
            <Loader2 size={16} className="text-emerald-600 animate-spin" />
            <span className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Getting preview...</span>
          </div>
        )}

        {/* Submit button — hidden after preview or execution success */}
        {!exec.hasPreview && !exec.isSuccess && (
          <button
            type="submit"
            data-testid="oms-btn-submit-order"
            disabled={isSubmitting || exec.isPreviewing}
            className={cn(
              "w-full py-4 rounded-xl text-sm font-black uppercase italic transition-all shadow-lg flex items-center justify-center gap-2",
              side === 'BUY'
                ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-600/20"
                : "bg-rose-600 hover:bg-rose-700 text-white shadow-rose-600/20"
            )}
          >
            {isSubmitting ? <Zap className="animate-pulse" size={18} /> : <Target size={18} />}
            {isSubmitting ? 'Routing...' : `Submit ${side} Order`}
          </button>
        )}
      </form>
    </div>
  );
}
